import * as Middleware from '@/Middleware';
import ChalkySticks from '@chalkysticks/sdk';
import Store from '@/Store';
import Vue from 'vue';
import VueRouter, { NavigationGuard, NavigationGuardNext, RawLocation, Route, RouteConfig } from 'vue-router';
import { IDispatcherEvent } from 'restmc';

// Attach VueRouter
// -----------------------------------------------------------------------------

Vue.use(VueRouter);

// Define Routing
// -----------------------------------------------------------------------------

const routes: Array<RouteConfig> = [
	{
		component: () => import('@/App/Page/Home.vue'),
		name: 'PageHome',
		path: '/',
	},
	{
		component: () => import('@/App/Page/Content.vue'),
		name: 'PageContent',
		path: '/content',
	},
	{
		component: () => import('@/App/Page/ContentItem.vue'),
		name: 'PageContentItem',
		path: '/content/:id',
		props: true,
	},
	{
		component: () => import('@/App/Page/Create.vue'),
		name: 'PageCreate',
		path: '/create',
	},
	{
		component: () => import('@/App/Page/Extras.vue'),
		name: 'PageExtras',
		path: '/extras',
	},
	{
		component: () => import('@/App/Page/Extras/BallDetector.vue'),
		name: 'PageExtrasBallDetector',
		path: '/extras/ball-detector',
	},
	{
		component: () => import('@/App/Page/Extras/Handicapper.vue'),
		name: 'PageExtrasHandicapper',
		path: '/extras/handicapper',
	},
	{
		component: () => import('@/App/Page/Extras/Killers.vue'),
		name: 'PageExtrasKillers',
		path: '/extras/killers',
	},
	{
		component: () => import('@/App/Page/Extras/Pad.vue'),
		name: 'PageExtrasPad',
		path: '/extras/pad',
	},
	{
		component: () => import('@/App/Page/Extras/RackRandomizer.vue'),
		name: 'PageExtrasRackRandomizer',
		path: '/extras/rack-randomizer',
	},
	{
		component: () => import('@/App/Page/Extras/Rulebook.vue'),
		name: 'PageExtrasRulebook',
		path: '/extras/rulebook',
	},
	{
		component: () => import('@/App/Page/Extras/Scorekeeper.vue'),
		name: 'PageExtrasScorekeeper',
		path: '/extras/scorekeeper',
	},
	{
		component: () => import('@/App/Page/Extras/ShotClock.vue'),
		name: 'PageExtrasShotClock',
		path: '/extras/shot-clock',
	},
	{
		component: () => import('@/App/Page/Map.vue'),
		name: 'PageMap',
		path: '/map',
	},
	{
		component: () => import('@/App/Page/Onboarding.vue'),
		name: 'PageOnboarding',
		path: '/onboarding',
	},
	{
		component: () => import('@/App/Page/Play.vue'),
		name: 'PagePlay',
		path: '/play/:id?',
	},
	{
		component: () => import('@/App/Page/Place.vue'),
		name: 'PagePlace',
		path: '/places/:id',
		props: true,
	},
	{
		component: () => import('@/App/Page/PlaceCheckins.vue'),
		name: 'PagePlaceCheckins',
		path: '/places/:id/checkins',
		props: true,
	},
	{
		component: () => import('@/App/Page/PlaceGallery.vue'),
		name: 'PagePlaceGallery',
		path: '/places/:id/gallery',
		props: true,
	},
	{
		component: () => import('@/App/Page/PlaceGalleryItem.vue'),
		name: 'PagePlaceGalleryItem',
		path: '/places/:id/gallery/:mediaId',
		props: true,
	},
	{
		component: () => import('@/App/Page/PlaceRevision.vue'),
		name: 'PagePlaceRevision',
		path: '/places/:id/revision',
		props: true,
	},
	{
		component: () => import('@/App/Page/Places.vue'),
		name: 'PagePlaces',
		path: '/places',
	},
	{
		component: () => import('@/App/Page/Redirect.vue'),
		name: 'PageRedirect',
		path: '/redirect*',
	},
	{
		component: () => import('@/App/Page/PasswordReset.vue'),
		name: 'PagePasswordReset',
		path: '/reset-password/:token',
		props: true,
	},
	{
		component: () => import('@/App/Page/Signup.vue'),
		name: 'PageSignup',
		path: '/signup/:step?',
	},
	{
		component: () => import('@/App/Page/Subscribe.vue'),
		name: 'PageSubscribe',
		path: '/subscribe',
	},
	{
		component: () => import('@/App/Page/TV.vue'),
		name: 'PageTVSpecific',
		path: '/tv/:id',
	},
	{
		component: () => import('@/App/Page/TV.vue'),
		name: 'PageTV',
		path: '/tv',
	},
	{
		component: () => import('@/App/Page/Logout.vue'),
		name: 'PageLogout',
		path: '/logout',
	},
	{
		component: () => import('@/App/Page/Styleguide.vue'),
		name: 'PageStyleguide',
		path: '/styleguide',
	},
	{
		component: () => import('@/App/Page/Test.vue'),
		name: 'PageTest',
		path: '/test',
	},
	{
		component: () => import('@/App/Page/Users.vue'),
		name: 'PageUsers',
		path: '/users',
	},
	{
		component: () => import('@/App/Page/User.vue'),
		name: 'PageUser',
		path: '/users/:id',
	},
	{
		component: () => import('@/App/Page/NotFound.vue'),
		name: 'PageNotFound',
		path: '*',
	},

	// {
	// 	redirect: '/',
	// },
];

// Setup Router
// -----------------------------------------------------------------------------

const router = new VueRouter({
	base: process.env.BASE_URL,
	mode: 'history',
	routes: routes,
});

export default router;

// Silence navigation duplication errors
// -----------------------------------------------------------------------------

const suppressDuplicate = (fn: any) =>
	function (...args: any[]) {
		// @ts-ignore
		return fn.apply(this, args)?.catch((err: any) => {
			if (err?.name === 'NavigationDuplicated' || err?.message?.includes('Avoided redundant navigation')) return;
			throw err;
		});
	};

router.push = suppressDuplicate(router.push);
router.replace = suppressDuplicate(router.replace);

// Middleware hooks
// ---------------------------------------------------------------------------

const middlewares: NavigationGuard[] = [
	Middleware.Onboarding,
	Middleware.QueryParameters,
	Middleware.Authentication,
	Middleware.ScrollToTop,
];

middlewares.forEach((middleware: NavigationGuard) => router.beforeEach(middleware));

// Global Navigation
// ---------------------------------------------------------------------------

ChalkySticks.Core.Event.Bus.on('view:user', (e: ChalkySticks.Core.IDispatcherEvent<ChalkySticks.Model.User>) => {
	Store.dispatch('gotoUser', e.data);
});

ChalkySticks.Core.Event.Bus.on('view:venue', (e: ChalkySticks.Core.IDispatcherEvent<ChalkySticks.Model.Venue>) => {
	Store.dispatch('gotoVenue', e.data);
});

ChalkySticks.Core.Event.Bus.on('signup:request', (e: ChalkySticks.Core.IDispatcherEvent<ChalkySticks.Model.Venue>) => {
	router.push({
		name: 'PageSignup',
	});
});
